import { Link } from 'react-router-dom';

/**
 * PrivacyPage - Public privacy policy
 * Required for Google OAuth verification
 */
export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950">
      {/* Header */}
      <header className="px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/welcome" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-glow flex items-center justify-center">
            <span className="text-xl">🌿</span>
          </div>
          <span className="font-display text-xl font-semibold text-white">The Wright Gardner</span>
        </Link>
      </header>

      {/* Content */}
      <main className="px-6 py-12 max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <h1 className="font-display text-3xl font-bold text-forest-900 mb-6">Privacy Policy</h1>
          <p className="text-sage-600 mb-4">Last updated: {new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })}</p>

          <div className="prose prose-sage max-w-none">
            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-sage-700 mb-4">
              When you use The Wright Gardner application, we collect information necessary to provide
              our landscape management services:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>Account information (name, email address)</li>
              <li>Service data (schedules, work orders, service logs)</li>
              <li>Location data for route optimization</li>
              <li>Communication data when you contact us or use email features</li>
            </ul>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">2. Gmail Integration</h2>
            <p className="text-sage-700 mb-4">
              If you choose to connect your Gmail account, we request access to send emails on your
              behalf. We use this access solely to:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>Send service-related emails to your clients</li>
              <li>Send work order notifications</li>
              <li>Send scheduling updates</li>
            </ul>
            <p className="text-sage-700 mb-4">
              We do not read, store, or access your Gmail inbox. We only store a record of emails
              sent through our platform for your reference.
            </p>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">3. QuickBooks Online Integration</h2>
            <p className="text-sage-700 mb-4">
              If you choose to connect your QuickBooks Online account, we request access to read and sync
              accounting data. We use this access to:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>Sync customer data between QuickBooks and our platform</li>
              <li>View and display invoices and payment information</li>
              <li>Display employee data for scheduling coordination</li>
              <li>Sync products and services catalog</li>
            </ul>
            <p className="text-sage-700 mb-4">
              Your QuickBooks data is:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>Stored securely in our encrypted database</li>
              <li>Only accessible to authorized users within your organization</li>
              <li>Never shared with third parties without your consent</li>
              <li>Deletable at any time by disconnecting the integration</li>
            </ul>
            <p className="text-sage-700 mb-4">
              You can disconnect QuickBooks at any time from your Settings page. When disconnected,
              we will delete all synced QuickBooks data from our systems.
            </p>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">4. How We Use Your Information</h2>
            <p className="text-sage-700 mb-4">
              We use collected information to:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>Provide and improve our landscape management services</li>
              <li>Optimize routes and schedules</li>
              <li>Enable communication with your clients</li>
              <li>Generate service reports and analytics</li>
            </ul>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">5. Data Security</h2>
            <p className="text-sage-700 mb-4">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>All data is encrypted in transit using HTTPS</li>
              <li>OAuth tokens are encrypted at rest using AES-256</li>
              <li>Access to systems is restricted and monitored</li>
            </ul>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">6. Data Sharing</h2>
            <p className="text-sage-700 mb-4">
              We do not sell your personal information. We may share data with:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>Service providers who help us operate our platform (e.g., cloud hosting)</li>
              <li>As required by law or to protect our rights</li>
            </ul>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">7. Your Rights</h2>
            <p className="text-sage-700 mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
              <li>Access your personal data</li>
              <li>Request deletion of your data</li>
              <li>Disconnect third-party integrations (like Gmail) at any time</li>
              <li>Export your data</li>
            </ul>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">8. Contact Us</h2>
            <p className="text-sage-700 mb-4">
              If you have questions about this privacy policy or your data, contact us at:
            </p>
            <p className="text-sage-700 mb-4">
              <strong>The Wright Gardner</strong><br />
              Email: privacy@thewrightgardner.com
            </p>

            <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">9. Changes to This Policy</h2>
            <p className="text-sage-700 mb-4">
              We may update this privacy policy from time to time. We will notify you of any
              significant changes by posting the new policy on this page.
            </p>
          </div>

          {/* EULA Section */}
          <div className="border-t border-sage-200 mt-12 pt-12">
            <h1 className="font-display text-3xl font-bold text-forest-900 mb-6">End-User License Agreement (EULA)</h1>
            <p className="text-sage-600 mb-4">Last updated: {new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' })}</p>

            <div className="prose prose-sage max-w-none">
              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">1. Agreement to Terms</h2>
              <p className="text-sage-700 mb-4">
                By accessing or using The Wright Gardner application ("the App"), you agree to be bound by these Terms.
                If you disagree with any part of these terms, you may not access the App.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">2. License Grant</h2>
              <p className="text-sage-700 mb-4">
                Subject to these Terms, The Wright Gardner grants you a limited, non-exclusive, non-transferable,
                revocable license to use the App for your internal business operations.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">3. Use Restrictions</h2>
              <p className="text-sage-700 mb-4">You agree not to:</p>
              <ul className="list-disc pl-6 text-sage-700 mb-4 space-y-2">
                <li>Sublicense, sell, or redistribute the App</li>
                <li>Reverse engineer, decompile, or disassemble the App</li>
                <li>Use the App for any unlawful purpose</li>
                <li>Attempt to gain unauthorized access to any systems</li>
                <li>Interfere with or disrupt the App's operation</li>
              </ul>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">4. Third-Party Integrations</h2>
              <p className="text-sage-700 mb-4">
                The App may integrate with third-party services such as QuickBooks Online, Google Workspace, and Gmail.
                Your use of these integrations is subject to the respective third-party terms of service. We are not
                responsible for the availability, accuracy, or content of third-party services.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">5. Data Ownership</h2>
              <p className="text-sage-700 mb-4">
                You retain ownership of all data you enter into the App. We do not claim ownership of your business data.
                You grant us a limited license to use your data solely to provide the App's services.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">6. Disclaimer of Warranties</h2>
              <p className="text-sage-700 mb-4">
                THE APP IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED,
                INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">7. Limitation of Liability</h2>
              <p className="text-sage-700 mb-4">
                IN NO EVENT SHALL THE WRIGHT GARDNER BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
                OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF THE APP.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">8. Termination</h2>
              <p className="text-sage-700 mb-4">
                We may terminate or suspend your access to the App immediately, without prior notice, for any reason,
                including breach of these Terms. Upon termination, your right to use the App will cease immediately.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">9. Governing Law</h2>
              <p className="text-sage-700 mb-4">
                These Terms shall be governed by the laws of the State of California, without regard to its conflict
                of law provisions.
              </p>

              <h2 className="font-display text-xl font-semibold text-forest-800 mt-8 mb-4">10. Contact</h2>
              <p className="text-sage-700 mb-4">
                For questions about these Terms, contact us at:<br />
                <strong>The Wright Gardner</strong><br />
                Email: legal@thewrightgardner.com
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8">
        <div className="max-w-6xl mx-auto text-center">
          <Link to="/welcome" className="text-forest-400 hover:text-forest-200 text-sm transition-colors">
            ← Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
}
